/**
 * An item's form: the plan's sections in order (forms/plan.ts), each a card, More collapsed until
 * it is opened or a field inside it is asked for. Sections are dumb views over the form context.
 */

import Icon from "@web-ui/components/Icon";
import { FieldControl } from "./controls/field-control";
import type { SectionId, SectionPlan } from "./plan";
import { AccessSection } from "./sections/access";
import { ArtworkSection } from "./sections/artwork";
import { ItemsSection, PartsSection, TermsSection } from "./sections/lists";
import { MomentsSection } from "./sections/moments";
import { RecipeSection } from "./sections/recipe";
import { StreamsSection, VideoSection } from "./sections/video";
import { useForm } from "./form-context";

export const sectionDomId = (id: SectionId) => `s-${id}`;

export function ItemForm({ moreOpen, setMoreOpen }: { moreOpen: boolean; setMoreOpen: (open: boolean) => void }) {
  const { plan, issuesUnder } = useForm();
  return (
    <>
      {plan.sections.map((section) => {
        if (section.id === "more") {
          const errors = section.fields.some((f) => issuesUnder(f.path.split(":")[0]!).some((i) => i.level === "error"));
          return (
            <section key="more" id={sectionDomId("more")} className="card flex flex-col gap-4" aria-labelledby="more-title">
              <h2 id="more-title" className="m-0">
                <button type="button" className="flex w-full items-center gap-3 rounded-2xl ps-card-inset text-start focus-visible:outline-2 focus-visible:outline-s-focus" aria-expanded={moreOpen} aria-controls="more-body" onClick={() => setMoreOpen(!moreOpen)}>
                  <span className="flex flex-col">
                    <span className="text-sub-title-1">{section.title}{errors ? <span className="ms-2 text-body-2 text-critical">· needs a look</span> : null}</span>
                    <span className="text-body-2 text-t-secondary">{section.intro}</span>
                  </span>
                  <Icon name="chevron" className={`ms-auto shrink-0 fill-t-secondary transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                </button>
              </h2>
              {moreOpen ? <div id="more-body" className="flex flex-col gap-5">{section.fields.map((f) => <FieldControl key={f.path} field={f} />)}</div> : null}
            </section>
          );
        }
        return (
          <section key={section.id} id={sectionDomId(section.id)} className="card flex flex-col gap-4" aria-labelledby={`${section.id}-title`}>
            <div className="ps-card-inset">
              <h2 id={`${section.id}-title`} className="text-sub-title-1">{section.title}</h2>
              {"intro" in section && section.intro ? <p className="text-body-2 text-t-secondary">{section.intro}</p> : null}
            </div>
            <SectionBody section={section} />
          </section>
        );
      })}
    </>
  );
}

function SectionBody({ section }: { section: SectionPlan }) {
  switch (section.id) {
    case "basics": return <div className="flex flex-col gap-5">{section.fields.map((f) => <FieldControl key={f.path} field={f} />)}</div>;
    case "video": return <VideoSection section={section} />;
    case "streams": return <StreamsSection section={section} />;
    case "moments": return <MomentsSection section={section} />;
    case "recipe": return <RecipeSection />;
    case "parts": return <PartsSection section={section} />;
    case "items": return <ItemsSection />;
    case "terms": return <TermsSection />;
    case "artwork": return <ArtworkSection section={section} />;
    case "access": return <AccessSection />;
    case "more": return null;
  }
}
